import {
  ArrowLeft,
  LoaderCircle,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'

import FormularioCompraPrevia from '../../components/abastecimiento/FormularioCompraPrevia'
import SelectorModalidad from '../../components/abastecimiento/SelectorModalidad'
import ConfirmarCompraModal from '../../components/compras/ConfirmarCompraModal'
import ModalMensaje from '../../components/ui/ModalMensaje'
import { useEmpresa } from '../../context/EmpresaContext'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import { puedeRegistrarCompra } from '../../utils/abastecimiento'
import {
  calcularPesaje,
  calcularResumenCompra,
  centavosAPesos,
  formatearEnteroCompra,
  formatearKilosCompra,
  formatearMontoCompra,
  milesimasAKilos,
  nombreProveedorCompra,
  normalizarCantidadCajas,
  pesoAMilesimas,
  pesoPalletAMilesimas,
  precioACentavos,
  taraAMilesimas,
} from '../../utils/compras'
import { obtenerFechaActualPos } from '../../utils/pos'

const MENSAJE_BRUTO_MENOR_TARA =
  'El peso bruto debe ser mayor que la tara total (Cajas/Rejillas y pallet).'

const estiloInput =
  'min-h-12 w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-base text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

const estiloCalculado =
  'flex min-h-12 w-full items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-base font-semibold text-slate-900'

function crearFormularioPesaje(tipoEnvaseId = '', pesoPallet = '0') {
  return {
    cantidadCajas: '',
    tipoEnvaseId,
    pesoPallet,
    pesoBruto: '',
    observacion: '',
  }
}

function formatearPesoPallet(milesimas) {
  return String(milesimasAKilos(milesimas ?? 0)).replace('.', ',')
}

function Campo({
  label,
  htmlFor,
  requerido = false,
  className = '',
  children,
}) {
  return (
    <div className={className}>
      <label
        htmlFor={htmlFor}
        className="mb-2 block text-sm font-semibold text-slate-700"
      >
        {label}
        {requerido && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      {children}
    </div>
  )
}

function FilaResumen({
  etiqueta,
  valor,
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <dt className="text-sm text-slate-500">
        {etiqueta}
      </dt>

      <dd className="text-right text-sm font-semibold tabular-nums text-slate-900">
        {valor}
      </dd>
    </div>
  )
}

function NuevaCompra() {
  const navigate = useNavigate()
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal, cerrarModal } = useModalMensaje()

  const empresaId = empresaActual?.id
  const esConsulta = !puedeRegistrarCompra(empresaActual?.rol)

  const [modalidad, setModalidad] = useState('DIRECTA')
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true)
  const [proveedores, setProveedores] = useState([])
  const [productos, setProductos] = useState([])
  const [tiposEnvase, setTiposEnvase] = useState([])

  const [proveedorId, setProveedorId] = useState('')
  const [fechaCompra, setFechaCompra] = useState(obtenerFechaActualPos())
  const [productoId, setProductoId] = useState('')
  const [documento, setDocumento] = useState('')
  const [precioKilo, setPrecioKilo] = useState('')

  const [formularioPesaje, setFormularioPesaje] = useState(
    crearFormularioPesaje()
  )
  const [errorPesaje, setErrorPesaje] = useState('')
  const [pesajeEditandoId, setPesajeEditandoId] = useState(null)
  const [pesajes, setPesajes] = useState([])

  const [confirmacionAbierta, setConfirmacionAbierta] = useState(false)
  const [registrando, setRegistrando] = useState(false)

  const contadorPesajes = useRef(0)
  const formularioPesajeRef = useRef(null)
  const inputCajasRef = useRef(null)

  useEffect(() => {
    if (!empresaId) return

    let cancelado = false

    const cargarCatalogos = async () => {
      setCargandoCatalogos(true)

      try {
        const [
          respuestaProveedores,
          respuestaProductos,
          respuestaEnvases,
        ] = await Promise.all([
          supabase
            .from('proveedores')
            .select('id, razon_social, nombre_fantasia, rut')
            .eq('empresa_id', empresaId)
            .eq('activo', true)
            .order('razon_social', { ascending: true }),
          supabase
            .from('productos')
            .select('id, codigo, nombre')
            .eq('empresa_id', empresaId)
            .eq('activo', true)
            .order('nombre', { ascending: true }),
          supabase
            .from('tipos_envase')
            .select('id, codigo, nombre, tara_kg')
            .eq('empresa_id', empresaId)
            .eq('activo', true)
            .order('nombre', { ascending: true }),
        ])

        if (respuestaProveedores.error) throw respuestaProveedores.error
        if (respuestaProductos.error) throw respuestaProductos.error
        if (respuestaEnvases.error) throw respuestaEnvases.error

        if (cancelado) return

        setProveedores(respuestaProveedores.data || [])
        setProductos(respuestaProductos.data || [])
        setTiposEnvase(respuestaEnvases.data || [])
      } catch (errorCarga) {
        console.error('Error al cargar datos para la compra:', errorCarga)

        if (cancelado) return

        abrirModal({
          tipo: 'error',
          titulo: 'Error al cargar datos',
          mensaje:
            errorCarga.message ||
            'No fue posible cargar proveedores, productos y Cajas/Rejillas.',
        })
      } finally {
        if (!cancelado) setCargandoCatalogos(false)
      }
    }

    // Al cambiar de empresa se descarta lo ingresado: proveedores,
    // productos y Cajas/Rejillas son de la empresa anterior.
    setProveedorId('')
    setProductoId('')
    setDocumento('')
    setPrecioKilo('')
    setPesajes([])
    setPesajeEditandoId(null)
    setFormularioPesaje(crearFormularioPesaje())
    setErrorPesaje('')

    cargarCatalogos()

    return () => {
      cancelado = true
    }
  }, [empresaId, abrirModal])

  const envasesPorId = useMemo(
    () =>
      new Map(tiposEnvase.map((tipo) => [tipo.id, tipo])),
    [tiposEnvase]
  )

  const precioCentavos = precioACentavos(precioKilo)

  const resumen = useMemo(
    () => calcularResumenCompra(pesajes, precioCentavos),
    [pesajes, precioCentavos]
  )

  // Vista previa en vivo del pesaje que se está ingresando.
  const vistaPrevia = useMemo(() => {
    const tipo = envasesPorId.get(formularioPesaje.tipoEnvaseId)
    const cantidadCajas = normalizarCantidadCajas(
      formularioPesaje.cantidadCajas
    )
    const pesoBrutoMilesimas = pesoAMilesimas(formularioPesaje.pesoBruto)

    if (!tipo || !Number.isFinite(cantidadCajas)) {
      return null
    }

    const taraUnitariaMilesimas = taraAMilesimas(tipo.tara_kg)
    const taraPalletMilesimas = pesoPalletAMilesimas(
      formularioPesaje.pesoPallet
    )

    if (!Number.isFinite(taraPalletMilesimas)) {
      return null
    }

    const taraTotalMilesimas =
      cantidadCajas * taraUnitariaMilesimas + taraPalletMilesimas

    if (!Number.isFinite(pesoBrutoMilesimas)) {
      return {
        taraTotalMilesimas,
        pesoNetoMilesimas: null,
      }
    }

    return calcularPesaje({
      cantidadCajas,
      taraUnitariaMilesimas,
      pesoBrutoMilesimas,
      taraPalletMilesimas,
    })
  }, [envasesPorId, formularioPesaje])

  const actualizarFormularioPesaje = (campo, valor) => {
    setFormularioPesaje((actual) => ({ ...actual, [campo]: valor }))
    setErrorPesaje('')
  }

  const enfocarFormularioPesaje = () => {
    formularioPesajeRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })

    window.setTimeout(() => {
      inputCajasRef.current?.focus({ preventScroll: true })
    }, 300)
  }

  const validarFormularioPesaje = () => {
    const cantidadCajas = normalizarCantidadCajas(
      formularioPesaje.cantidadCajas
    )

    if (!Number.isFinite(cantidadCajas)) {
      return {
        error:
          'La cantidad de cajas debe ser un número entero mayor a cero.',
      }
    }

    const tipo = envasesPorId.get(formularioPesaje.tipoEnvaseId)

    if (!tipo) {
      return { error: 'Debe seleccionar una Caja/Rejilla.' }
    }

    if (!formularioPesaje.pesoBruto.trim()) {
      return { error: 'Debe ingresar el peso bruto.' }
    }

    const pesoBrutoMilesimas = pesoAMilesimas(formularioPesaje.pesoBruto)

    if (!Number.isFinite(pesoBrutoMilesimas)) {
      return {
        error:
          'Peso bruto no válido. Usa coma o punto decimal, con máximo 3 decimales (ej: 125,5).',
      }
    }

    if (pesoBrutoMilesimas <= 0) {
      return { error: 'El peso bruto debe ser mayor a cero.' }
    }

    const taraUnitariaMilesimas = taraAMilesimas(tipo.tara_kg)

    // Peso del pallet: opcional (vacío = 0), entre 0 y 10.000 kg.
    const taraPalletMilesimas = pesoPalletAMilesimas(
      formularioPesaje.pesoPallet
    )

    if (!Number.isFinite(taraPalletMilesimas)) {
      return {
        error:
          'Peso del pallet no válido. Debe estar entre 0 y 10.000 kg, sin negativos y con máximo 3 decimales.',
      }
    }

    const { taraTotalMilesimas, pesoNetoMilesimas } = calcularPesaje({
      cantidadCajas,
      taraUnitariaMilesimas,
      pesoBrutoMilesimas,
      taraPalletMilesimas,
    })

    if (pesoNetoMilesimas <= 0) {
      return { error: MENSAJE_BRUTO_MENOR_TARA }
    }

    return {
      pesaje: {
        cantidadCajas,
        tipoEnvaseId: tipo.id,
        envaseNombre: tipo.nombre,
        taraPalletMilesimas,
        taraUnitariaMilesimas,
        pesoBrutoMilesimas,
        taraTotalMilesimas,
        pesoNetoMilesimas,
        observacion: formularioPesaje.observacion.trim(),
      },
    }
  }

  const guardarPesaje = (event) => {
    event?.preventDefault()

    const { error, pesaje } = validarFormularioPesaje()

    if (error) {
      setErrorPesaje(error)
      return
    }

    if (pesajeEditandoId) {
      setPesajes((actuales) =>
        actuales.map((item) =>
          item.id === pesajeEditandoId
            ? { ...pesaje, id: item.id }
            : item
        )
      )
      setPesajeEditandoId(null)
    } else {
      contadorPesajes.current += 1

      setPesajes((actuales) => [
        ...actuales,
        { ...pesaje, id: contadorPesajes.current },
      ])
    }

    // Se mantiene la Caja/Rejilla elegida: en una misma compra los
    // pesajes suelen repetir el mismo envase.
    setFormularioPesaje(
      crearFormularioPesaje(
        pesaje.tipoEnvaseId,
        formatearPesoPallet(pesaje.taraPalletMilesimas)
      )
    )
    setErrorPesaje('')

    window.setTimeout(() => {
      inputCajasRef.current?.focus({ preventScroll: true })
    }, 0)
  }

  const editarPesaje = (pesaje) => {
    setPesajeEditandoId(pesaje.id)
    setFormularioPesaje({
      cantidadCajas: String(pesaje.cantidadCajas),
      tipoEnvaseId: pesaje.tipoEnvaseId,
      pesoPallet: formatearPesoPallet(pesaje.taraPalletMilesimas),
      pesoBruto: milesimasAKilos(pesaje.pesoBrutoMilesimas)
        .toString()
        .replace('.', ','),
      observacion: pesaje.observacion || '',
    })
    setErrorPesaje('')
    enfocarFormularioPesaje()
  }

  const cancelarEdicion = () => {
    setPesajeEditandoId(null)
    setFormularioPesaje((actual) =>
      crearFormularioPesaje(actual.tipoEnvaseId, actual.pesoPallet)
    )
    setErrorPesaje('')
  }

  const solicitarEliminarPesaje = (pesaje, numero) => {
    abrirModal({
      tipo: 'confirm',
      titulo: `Eliminar pesaje ${numero}`,
      mensaje: `${pesaje.cantidadCajas} cajas · ${pesaje.envaseNombre} · ${formatearKilosCompra(
        milesimasAKilos(pesaje.pesoBrutoMilesimas)
      )} bruto. Los pesajes restantes se renumerarán.`,
      mostrarCancelar: true,
      textoAceptar: 'Eliminar',
      onAceptar: () => {
        setPesajes((actuales) =>
          actuales.filter((item) => item.id !== pesaje.id)
        )

        if (pesajeEditandoId === pesaje.id) {
          cancelarEdicion()
        }

        cerrarModal()
      },
    })
  }

  const mostrarError = (mensaje) => {
    abrirModal({
      tipo: 'error',
      titulo: 'Revisa la compra',
      mensaje,
    })
  }

  const validarCompra = () => {
    if (!proveedorId) return 'Debe seleccionar un proveedor.'
    if (!fechaCompra) return 'Debe ingresar la fecha de la compra.'
    if (!productoId) return 'Debe seleccionar un producto.'

    if (!precioKilo.trim()) return 'Debe ingresar el precio por kg.'

    if (!Number.isFinite(precioCentavos)) {
      return 'Precio/kg no válido. Ejemplos: 750, 2.500 o 750,50 (máximo 2 decimales).'
    }

    if (precioCentavos <= 0) return 'Debe ingresar el precio por kg.'

    if (pesajes.length === 0) return 'Debe agregar al menos un pesaje.'

    if (pesajeEditandoId) {
      return 'Termina o cancela la edición del pesaje antes de guardar la compra.'
    }

    return ''
  }

  const solicitarGuardar = () => {
    const error = validarCompra()

    if (error) {
      mostrarError(error)
      return
    }

    setConfirmacionAbierta(true)
  }

  const registrarCompra = async () => {
    if (registrando) return

    setRegistrando(true)

    try {
      // Solo se envían los datos de entrada: tara, neto y total los
      // recalcula Supabase con la tara vigente del maestro.
      const { data: compraId, error } = await supabase.rpc(
        'registrar_compra',
        {
          p_empresa_id: empresaId,
          p_proveedor_id: proveedorId,
          p_fecha_compra: fechaCompra,
          p_producto_id: productoId,
          p_documento: documento.trim() || null,
          p_precio_kilo: centavosAPesos(precioCentavos),
          p_pesajes: pesajes.map((pesaje) => ({
            cantidad_cajas: pesaje.cantidadCajas,
            tipo_envase_id: pesaje.tipoEnvaseId,
            peso_pallet_kg: milesimasAKilos(pesaje.taraPalletMilesimas),
            peso_bruto_kg: milesimasAKilos(pesaje.pesoBrutoMilesimas),
            observacion: pesaje.observacion || null,
          })),
        }
      )

      if (error) throw error

      setConfirmacionAbierta(false)

      abrirModal({
        tipo: 'success',
        titulo: 'Compra registrada',
        mensaje: 'Compra registrada correctamente.',
        textoAceptar: 'Ver compra',
        onAceptar: () => {
          cerrarModal()
          navigate(`/compras/${compraId}`, { replace: true })
        },
      })
    } catch (errorRegistro) {
      console.error('Error al registrar compra:', errorRegistro)

      setConfirmacionAbierta(false)

      abrirModal({
        tipo: 'error',
        titulo: 'No fue posible registrar la compra',
        mensaje:
          errorRegistro.message ||
          'Ocurrió un error al registrar la compra.',
      })
    } finally {
      setRegistrando(false)
    }
  }

  const proveedorSeleccionado = proveedores.find(
    (proveedor) => proveedor.id === proveedorId
  )
  const productoSeleccionado = productos.find(
    (producto) => producto.id === productoId
  )

  const resumenConfirmacion = {
    proveedor: nombreProveedorCompra(proveedorSeleccionado),
    producto: productoSeleccionado?.nombre || '—',
    cantidadCajas: formatearEnteroCompra(resumen.cantidadCajas),
    kilosNetos: formatearKilosCompra(
      milesimasAKilos(resumen.pesoNetoMilesimas)
    ),
    precioKilo: formatearMontoCompra(
      Number.isFinite(precioCentavos) ? centavosAPesos(precioCentavos) : 0
    ),
    totalCompra: formatearMontoCompra(
      centavosAPesos(resumen.totalCompraCentavos)
    ),
  }

  if (esConsulta) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <h3 className="text-lg font-bold text-slate-900">
          Sin permisos
        </h3>

        <p className="mt-2 text-slate-500">
          Tu perfil no tiene permisos para registrar compras.
        </p>
      </div>
    )
  }

  if (cargandoCatalogos) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <LoaderCircle
            size={36}
            className="mx-auto animate-spin text-emerald-600"
          />

          <p className="mt-4 font-medium text-slate-500">
            Cargando datos...
          </p>
        </div>
      </div>
    )
  }

  const bloqueado = registrando

  const encabezado = (
    <>
      <button
        type="button"
        onClick={() => navigate('/compras')}
        className="mb-5 flex min-h-11 items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
      >
        <ArrowLeft size={18} />
        Volver a abastecimiento
      </button>

      <div className="mb-6 sm:mb-8">
        <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
          Abastecimiento
        </p>

        <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
          Nueva compra
        </h2>

        <p className="mt-2 text-slate-500">
          Elige el tipo de compra y registra los datos del proveedor o
          agricultor.
        </p>
      </div>

      <SelectorModalidad
        valor={modalidad}
        onCambiar={setModalidad}
        deshabilitado={bloqueado}
      />
    </>
  )

  if (modalidad === 'PREVIA') {
    return (
      <div className="w-full">
        {encabezado}

        <FormularioCompraPrevia
          empresaId={empresaId}
          proveedores={proveedores}
          productos={productos}
        />
      </div>
    )
  }

  return (
    <div className="w-full">
      {encabezado}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          {/* ENCABEZADO */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2">
              <Campo
                label="Proveedor / Agricultor"
                htmlFor="compra-proveedor"
                requerido
                className="md:col-span-2"
              >
                <select
                  id="compra-proveedor"
                  value={proveedorId}
                  onChange={(event) => setProveedorId(event.target.value)}
                  disabled={bloqueado}
                  className={estiloInput}
                >
                  <option value="">Seleccionar proveedor</option>
                  {proveedores.map((proveedor) => (
                    <option
                      key={proveedor.id}
                      value={proveedor.id}
                    >
                      {nombreProveedorCompra(proveedor)}
                    </option>
                  ))}
                </select>

                {proveedores.length === 0 && (
                  <p className="mt-2 text-sm text-amber-700">
                    No hay proveedores activos. Créalos en Mantenedores → Proveedores.
                  </p>
                )}
              </Campo>

              <Campo
                label="Fecha"
                htmlFor="compra-fecha"
                requerido
              >
                <input
                  id="compra-fecha"
                  type="date"
                  value={fechaCompra}
                  onChange={(event) => setFechaCompra(event.target.value)}
                  disabled={bloqueado}
                  className={estiloInput}
                />
              </Campo>

              <Campo
                label="Producto"
                htmlFor="compra-producto"
                requerido
              >
                <select
                  id="compra-producto"
                  value={productoId}
                  onChange={(event) => setProductoId(event.target.value)}
                  disabled={bloqueado}
                  className={estiloInput}
                >
                  <option value="">Seleccionar producto</option>
                  {productos.map((producto) => (
                    <option
                      key={producto.id}
                      value={producto.id}
                    >
                      {producto.nombre}
                    </option>
                  ))}
                </select>
              </Campo>

              <Campo
                label="Documento"
                htmlFor="compra-documento"
              >
                <input
                  id="compra-documento"
                  type="text"
                  value={documento}
                  onChange={(event) => setDocumento(event.target.value)}
                  placeholder="Ej: Guía 12345"
                  maxLength={120}
                  disabled={bloqueado}
                  className={estiloInput}
                />
              </Campo>

              <Campo
                label="Precio/kg"
                htmlFor="compra-precio"
                requerido
              >
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-slate-400">
                    $
                  </span>

                  <input
                    id="compra-precio"
                    type="text"
                    inputMode="decimal"
                    value={precioKilo}
                    onChange={(event) => setPrecioKilo(event.target.value)}
                    placeholder="750"
                    disabled={bloqueado}
                    className={`${estiloInput} pl-7`}
                  />
                </div>

                {precioKilo.trim() && !Number.isFinite(precioCentavos) && (
                  <p className="mt-2 text-sm text-red-600">
                    Precio no válido. Ejemplos: 750, 2.500 o 750,50.
                  </p>
                )}
              </Campo>
            </div>
          </section>

          {/* DETALLE DE PESAJE: formulario */}
          <section
            ref={formularioPesajeRef}
            className={`scroll-mt-24 rounded-2xl border bg-white p-4 shadow-sm sm:p-6 ${
              pesajeEditandoId
                ? 'border-amber-300 ring-2 ring-amber-100'
                : 'border-slate-200'
            }`}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                {pesajeEditandoId
                  ? `Editando pesaje ${
                      pesajes.findIndex(
                        (pesaje) => pesaje.id === pesajeEditandoId
                      ) + 1
                    }`
                  : 'Detalle de pesaje'}
              </h3>

              {!pesajeEditandoId && (
                <span className="text-xs font-semibold text-slate-400">
                  Pesaje {pesajes.length + 1}
                </span>
              )}
            </div>

            <form
              onSubmit={guardarPesaje}
              noValidate
            >
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
                <Campo
                  label="Cajas"
                  htmlFor="pesaje-cajas"
                  requerido
                >
                  <input
                    id="pesaje-cajas"
                    ref={inputCajasRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formularioPesaje.cantidadCajas}
                    onChange={(event) =>
                      actualizarFormularioPesaje(
                        'cantidadCajas',
                        event.target.value
                      )
                    }
                    placeholder="8"
                    disabled={bloqueado}
                    className={estiloInput}
                  />
                </Campo>

                <Campo
                  label="Peso bruto (kg)"
                  htmlFor="pesaje-bruto"
                  requerido
                >
                  <input
                    id="pesaje-bruto"
                    type="text"
                    inputMode="decimal"
                    value={formularioPesaje.pesoBruto}
                    onChange={(event) =>
                      actualizarFormularioPesaje(
                        'pesoBruto',
                        event.target.value
                      )
                    }
                    placeholder="125,5"
                    disabled={bloqueado}
                    className={estiloInput}
                  />
                </Campo>

                <Campo
                  label="Caja/Rejilla"
                  htmlFor="pesaje-envase"
                  requerido
                  className="col-span-2"
                >
                  <select
                    id="pesaje-envase"
                    value={formularioPesaje.tipoEnvaseId}
                    onChange={(event) =>
                      actualizarFormularioPesaje(
                        'tipoEnvaseId',
                        event.target.value
                      )
                    }
                    disabled={bloqueado}
                    className={estiloInput}
                  >
                    <option value="">Seleccionar Caja/Rejilla</option>
                    {tiposEnvase.map((tipo) => (
                      <option
                        key={tipo.id}
                        value={tipo.id}
                      >
                        {tipo.nombre} · tara {formatearKilosCompra(tipo.tara_kg)}
                      </option>
                    ))}
                  </select>
                </Campo>

                <Campo
                  label="Peso del pallet (kg)"
                  htmlFor="pesaje-pallet"
                  className="col-span-2"
                >
                  <input
                    id="pesaje-pallet"
                    type="text"
                    inputMode="decimal"
                    value={formularioPesaje.pesoPallet}
                    onChange={(event) =>
                      actualizarFormularioPesaje(
                        'pesoPallet',
                        event.target.value
                      )
                    }
                    placeholder="0"
                    disabled={bloqueado}
                    className={estiloInput}
                  />
                </Campo>

                <Campo label="Tara">
                  <p className={estiloCalculado}>
                    {vistaPrevia
                      ? formatearKilosCompra(
                          milesimasAKilos(vistaPrevia.taraTotalMilesimas)
                        )
                      : '—'}
                  </p>
                </Campo>

                <Campo label="Peso neto">
                  <p
                    className={`${estiloCalculado} ${
                      vistaPrevia?.pesoNetoMilesimas !== null &&
                      vistaPrevia?.pesoNetoMilesimas !== undefined &&
                      vistaPrevia.pesoNetoMilesimas <= 0
                        ? 'text-red-600'
                        : ''
                    }`}
                  >
                    {vistaPrevia?.pesoNetoMilesimas !== null &&
                    vistaPrevia?.pesoNetoMilesimas !== undefined
                      ? formatearKilosCompra(
                          milesimasAKilos(vistaPrevia.pesoNetoMilesimas)
                        )
                      : '—'}
                  </p>
                </Campo>

                <Campo
                  label="Observación"
                  htmlFor="pesaje-observacion"
                  className="col-span-2"
                >
                  <input
                    id="pesaje-observacion"
                    type="text"
                    value={formularioPesaje.observacion}
                    onChange={(event) =>
                      actualizarFormularioPesaje(
                        'observacion',
                        event.target.value
                      )
                    }
                    placeholder="Opcional"
                    maxLength={500}
                    disabled={bloqueado}
                    className={estiloInput}
                  />
                </Campo>
              </div>

              {vistaPrevia?.pesoNetoMilesimas !== null &&
                vistaPrevia?.pesoNetoMilesimas !== undefined &&
                vistaPrevia.pesoNetoMilesimas <= 0 &&
                !errorPesaje && (
                  <p className="mt-3 text-sm text-red-600">
                    {MENSAJE_BRUTO_MENOR_TARA}
                  </p>
                )}

              {errorPesaje && (
                <p
                  role="alert"
                  className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                >
                  {errorPesaje}
                </p>
              )}

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-end">
                {pesajeEditandoId && (
                  <button
                    type="button"
                    onClick={cancelarEdicion}
                    disabled={bloqueado}
                    className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    <X size={18} />
                    Cancelar edición
                  </button>
                )}

                <button
                  type="submit"
                  disabled={bloqueado}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
                >
                  {pesajeEditandoId ? (
                    <>
                      <Save size={18} />
                      Guardar cambios
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      Agregar pesaje
                    </>
                  )}
                </button>
              </div>
            </form>
          </section>

          {/* DETALLE DE PESAJE: agregados */}
          {pesajes.length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
                Pesajes agregados ({pesajes.length})
              </h3>

              {/* Mobile: tarjetas */}
              <div className="space-y-3 md:hidden">
                {pesajes.map((pesaje, indice) => (
                  <div
                    key={pesaje.id}
                    className={`rounded-xl border p-4 ${
                      pesaje.id === pesajeEditandoId
                        ? 'border-amber-300 bg-amber-50'
                        : 'border-slate-200'
                    }`}
                  >
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                      Pesaje {indice + 1}
                    </p>

                    <p className="mt-1 font-semibold text-slate-900">
                      {pesaje.cantidadCajas} cajas · {pesaje.envaseNombre}
                    </p>

                    {pesaje.taraPalletMilesimas > 0 && (
                      <p className="text-sm text-slate-600">
                        Pallet:{' '}
                        {formatearKilosCompra(
                          milesimasAKilos(pesaje.taraPalletMilesimas)
                        )}
                      </p>
                    )}

                    <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
                      <div className="min-w-0">
                        <dt className="text-xs text-slate-500">Bruto</dt>
                        <dd className="font-semibold tabular-nums text-slate-800">
                          {formatearKilosCompra(
                            milesimasAKilos(pesaje.pesoBrutoMilesimas)
                          )}
                        </dd>
                      </div>

                      <div className="min-w-0">
                        <dt className="text-xs text-slate-500">Tara</dt>
                        <dd className="font-semibold tabular-nums text-slate-800">
                          {formatearKilosCompra(
                            milesimasAKilos(pesaje.taraTotalMilesimas)
                          )}
                        </dd>
                      </div>

                      <div className="min-w-0">
                        <dt className="text-xs text-slate-500">Neto</dt>
                        <dd className="font-bold tabular-nums text-emerald-700">
                          {formatearKilosCompra(
                            milesimasAKilos(pesaje.pesoNetoMilesimas)
                          )}
                        </dd>
                      </div>
                    </dl>

                    {pesaje.observacion && (
                      <p className="mt-2 break-words text-sm text-slate-500">
                        {pesaje.observacion}
                      </p>
                    )}

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => editarPesaje(pesaje)}
                        disabled={bloqueado}
                        className="flex min-h-11 items-center justify-center gap-2 rounded-lg border border-emerald-600 px-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Pencil size={16} />
                        Editar
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          solicitarEliminarPesaje(pesaje, indice + 1)
                        }
                        disabled={bloqueado}
                        className="flex min-h-11 items-center justify-center gap-2 rounded-lg border border-red-200 px-3 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Trash2 size={16} />
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tablet / desktop: tabla compacta */}
              <div className="hidden w-full overflow-x-auto md:block">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="py-2 pr-3">N°</th>
                      <th className="py-2 pr-3 text-right">Cajas</th>
                      <th className="py-2 pr-3">Caja/Rejilla</th>
                      <th className="py-2 pr-3 text-right">Bruto</th>
                      <th className="py-2 pr-3 text-right">Tara</th>
                      <th className="py-2 pr-3 text-right">Neto</th>
                      <th className="py-2 pr-3">Observación</th>
                      <th className="py-2 text-right">Acciones</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {pesajes.map((pesaje, indice) => (
                      <tr
                        key={pesaje.id}
                        className={
                          pesaje.id === pesajeEditandoId
                            ? 'bg-amber-50'
                            : ''
                        }
                      >
                        <td className="py-2 pr-3 font-semibold text-slate-700">
                          {indice + 1}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {pesaje.cantidadCajas}
                        </td>
                        <td className="py-2 pr-3 text-slate-700">
                          {pesaje.envaseNombre}
                          {pesaje.taraPalletMilesimas > 0 && (
                            <span className="block text-xs text-slate-500">
                              Pallet:{' '}
                              {formatearKilosCompra(
                                milesimasAKilos(pesaje.taraPalletMilesimas)
                              )}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                          {formatearKilosCompra(
                            milesimasAKilos(pesaje.pesoBrutoMilesimas)
                          )}
                        </td>
                        <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-slate-500">
                          {formatearKilosCompra(
                            milesimasAKilos(pesaje.taraTotalMilesimas)
                          )}
                        </td>
                        <td className="whitespace-nowrap py-2 pr-3 text-right font-semibold tabular-nums text-slate-900">
                          {formatearKilosCompra(
                            milesimasAKilos(pesaje.pesoNetoMilesimas)
                          )}
                        </td>
                        <td className="max-w-[12rem] truncate py-2 pr-3 text-slate-500">
                          {pesaje.observacion || '—'}
                        </td>
                        <td className="py-2">
                          <div className="flex justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => editarPesaje(pesaje)}
                              disabled={bloqueado}
                              aria-label={`Editar pesaje ${indice + 1}`}
                              title="Editar"
                              className="rounded-lg p-2 text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <Pencil size={16} />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                solicitarEliminarPesaje(pesaje, indice + 1)
                              }
                              disabled={bloqueado}
                              aria-label={`Eliminar pesaje ${indice + 1}`}
                              title="Eliminar"
                              className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!pesajeEditandoId && (
                <button
                  type="button"
                  onClick={enfocarFormularioPesaje}
                  disabled={bloqueado}
                  className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-emerald-600 px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 md:hidden"
                >
                  <Plus size={18} />
                  Agregar otro pesaje
                </button>
              )}
            </section>
          )}
        </div>

        {/* RESUMEN */}
        <aside className="min-w-0">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6 lg:sticky lg:top-28">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Resumen
            </h3>

            <dl className="mt-3 divide-y divide-slate-100">
              <FilaResumen
                etiqueta="Pesajes"
                valor={formatearEnteroCompra(resumen.cantidadPesajes)}
              />

              <FilaResumen
                etiqueta="Cajas"
                valor={formatearEnteroCompra(resumen.cantidadCajas)}
              />

              <FilaResumen
                etiqueta="Peso bruto"
                valor={formatearKilosCompra(
                  milesimasAKilos(resumen.pesoBrutoMilesimas)
                )}
              />

              <FilaResumen
                etiqueta="Tara"
                valor={formatearKilosCompra(
                  milesimasAKilos(resumen.taraTotalMilesimas)
                )}
              />

              <FilaResumen
                etiqueta="Peso neto"
                valor={formatearKilosCompra(
                  milesimasAKilos(resumen.pesoNetoMilesimas)
                )}
              />

              <FilaResumen
                etiqueta="Precio/kg"
                valor={
                  Number.isFinite(precioCentavos)
                    ? formatearMontoCompra(centavosAPesos(precioCentavos))
                    : '—'
                }
              />
            </dl>

            <div className="mt-3 flex items-center justify-between gap-4 rounded-xl bg-emerald-50 px-4 py-3">
              <span className="text-sm font-bold uppercase tracking-wide text-emerald-800">
                Monto provisional
              </span>

              <span className="text-right text-xl font-bold tabular-nums text-emerald-700">
                {formatearMontoCompra(
                  centavosAPesos(resumen.totalCompraCentavos)
                )}
              </span>
            </div>

            <p className="mt-2 text-xs text-slate-500">
              Es provisional: el importe definitivo se calcula al validar
              la recepción, con los kilos aceptados.
            </p>

            <button
              type="button"
              onClick={solicitarGuardar}
              disabled={bloqueado}
              className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
            >
              {registrando ? (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              ) : (
                <Save size={18} />
              )}
              Guardar compra
            </button>
          </section>
        </aside>
      </div>

      <ConfirmarCompraModal
        abierto={confirmacionAbierta}
        resumen={resumenConfirmacion}
        procesando={registrando}
        onCancelar={() => setConfirmacionAbierta(false)}
        onConfirmar={registrarCompra}
      />

      <ModalMensaje {...modal} />
    </div>
  )
}

export default NuevaCompra
